import {FC, KeyboardEvent, useEffect, useMemo, useState} from 'react'
import {
  API_ROOT,
  API_URL,
  hasAutomaticInvoiceCapability,
  onTSP,
} from '../../../../../../_metronic/helpers'
import clsx from 'clsx'
import {useAuth} from '../../../../auth'
import axios from 'axios'
import {Link} from 'react-router-dom'
import {CustomInputs} from '../../../../../../_metronic/helpers/components/CustomInputs'
import {ExcelColumnGuideHint} from '../../../../../../_metronic/helpers/components/ExcelColumnGuideHint'
import {ICompanytemplate} from '../../../taxpayer-management/taxpayers-setting/_models'
import {SearchSelectOption} from '../../../../../../_metronic/helpers/components/SearchSelect'
import {INVOICE_ENTRY_TYPE, resolveInvoiceAutoSendControlValue} from '../core/invoiceAutoSend'
import {useInvoiceAutoSendDefault} from '../core/useInvoiceAutoSendDefault'
import {InvoiceExcelArticle9Notice} from '../components/InvoiceExcelArticle9Notice'
import {isOnPremisesInstallation} from '../../../../auth/core/onPremisesAccess'
import {ServiceFeeLine} from '../../../../business-services/components/ServiceFeeLine'
import {ChargePrompt, InlineChargeConfirm} from '../../../../business-services/components/ServiceParts'
import {
  CHARGE_CONFIRM,
  readServiceError,
  WALLET_INSUFFICIENT,
} from '../../../../business-services/core/serviceApi'
import {WalletShortfallAction} from '../../../../online-payment/wallet/WalletTopUp'
import './InvoiceExcelModal.css'

export const POST_INVOICE_EXCEL_URL = `${API_URL}/invoices/excel`

type ExcelImportMode = 'old' | 'new' | 'automatic'
type ExcelUploadStatus = 'idle' | 'uploading' | 'queued' | 'error'

const queuedUploadMessage =
  'فایل اکسل دریافت شد و پردازش آن در صف قرار گرفت. نتیجه عملیات را از صفحه لاگ‌ها پیگیری کنید.'
const uploadErrorMessage =
  'فایل اکسل پردازش نشد. لطفاً متن خطا را بررسی کنید و پس از اصلاح فایل، دوباره آن را بارگذاری کنید.'

const normalizeUploadMessage = (message: string) =>
  message
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\r\n|\r/g, '\n')
    .trim()

const readResponseMessage = (payload: any, fallback: string) => {
  const message =
    typeof payload == 'string'
      ? payload
      : payload?.message || payload?.Message || payload?.error || payload?.Error

  return normalizeUploadMessage((message || fallback).toString())
}

const readUploadErrorMessage = (error: any) => {
  if (!error?.response && error?.message === 'Network Error') {
    return 'ارتباط با سرور هنگام ثبت درخواست قطع شد. قبل از بارگذاری دوباره، صفحه لاگ‌ها را بررسی کنید؛ ممکن است فایل در صف ثبت شده باشد.'
  }

  return readResponseMessage(
    error?.response?.data,
    error?.message || 'خطا در بارگذاری فایل اکسل. لطفاً دوباره تلاش کنید.'
  )
}

// The old 1-2 and 1-3 files are no longer offered: the backend's old-version importer has no
// currency-sale branch, and its type-one gold branch expects sheets the 1-3 file does not have.
const oldDraftKeys = ['1-1', '1-4', '1-7', '1-14', '2-1', '2-3']

const newDraftVersions: Record<string, number> = {
  '1-1': 8,
  '1-2': 7,
  '1-3': 2,
  '1-4': 8,
  '1-5': 2,
  '1-6': 2,
  '1-7': 3,
  '1-8': 1,
  '1-9': 1,
  '1-11': 1,
  '1-13': 1,
  '1-14': 1,
  '2-1': 8,
  '2-3': 2,
  '2-9': 1,
  '2-13': 1,
}

// الگوی ۱۱ (بورس کالا) عمداً حذف شده است: صورتحساب خودکار/قیمت تمام‌شده مبلغ را خودش می‌سازد و
// اعلامیه فروش بورس ندارد، پس هرگز الگوی ۱۱ معتبر نمی‌شود (Backend هم آن را رد می‌کند).
const automaticSupportedTemplates = [1, 2, 3, 4, 5, 6, 7, 8, 9, 13, 14]
const automaticDraftFileName = 'AutomaticInvoiceDraft.xlsx'
const automaticTypeOneDraftFileName = 'AutomaticInvoiceDraftType1.xlsx'

const getDraftKey = (invoiceType: number, invoiceTemplate: number) =>
  `${invoiceType}-${invoiceTemplate}`

const padVersion = (version: number) => version.toString().padStart(2, '0')

type props = {
  action: {
    excelModal: boolean
    setExcelModal: React.Dispatch<React.SetStateAction<boolean>>
  }
  invoiceType: number
  invoiceTemplate: number
}

const InvoiceExcelModalForm: FC<props> = ({action, invoiceType, invoiceTemplate}) => {
  const {currentUser} = useAuth()
  const isOnPremises = isOnPremisesInstallation(currentUser)
  const currentCompany = currentUser?.cpl?.find((x) => x.id == currentUser.cpi)
  const companyFormModes = currentCompany?.frm

  const [file, setFile] = useState<File | null>(null)
  const [uploadStatus, setUploadStatus] = useState<ExcelUploadStatus>('idle')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [serverMessage, setServerMessage] = useState('')
  const [selectedFileName, setSelectedFileName] = useState('')

  const [templateOptions, setTemplateOptions] = useState<ICompanytemplate[]>([])
  const [templateItems, setTemplateItems] = useState<SearchSelectOption[]>([])
  const [printTemplate, setPrintTemplate] = useState<number>(0)
  const [send, setSend] = useState(false)
  const [sendTouched, setSendTouched] = useState(false)
  const [vatCheck, setVatCheck] = useState(1)
  const [validationMode, setValidationMode] = useState(0)
  // «بررسی پرونده خریداران نوع ۲» (1405/07/30): type-2 natural buyers are asked too, within the file's five.
  const [checkTypeTwoBuyers, setCheckTypeTwoBuyers] = useState(false)
  const [proforma, setProforma] = useState(false)
  // A file of proformas is agreed to once (user decision 2026-10-16): the file waits here with the fee to agree to.
  const [proformaCharge, setProformaCharge] = useState<{file: File; prompt: ChargePrompt}>()
  // A short wallet (409 WALLET_INSUFFICIENT_BALANCE): its balance and what the file needed, for the shortfall. The
  // upload is not resumed after the charge: a chosen file cannot be kept across the gateway, so it is chosen again.
  const [walletShort, setWalletShort] = useState<{balance?: number; required?: number}>()
  const [importMode, setImportMode] = useState<ExcelImportMode>('new')
  const autoSendEntryType =
    importMode == 'automatic' ? INVOICE_ENTRY_TYPE.automaticInvoice : INVOICE_ENTRY_TYPE.excel
  const {defaultAutoSend} = useInvoiceAutoSendDefault(autoSendEntryType, currentUser?.cpi)

  const displayedSend = resolveInvoiceAutoSendControlValue(defaultAutoSend, send, sendTouched)

  const setSendFromControl: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    setSendTouched(true)
    setSend(value)
  }

  const rnd = useMemo(() => Math.floor(Math.random() * 561518), [])
  const draftKey = getDraftKey(invoiceType, invoiceTemplate)
  const hasOldDraft = oldDraftKeys.includes(draftKey)
  const latestVersion = newDraftVersions[draftKey]
  const hasNewDraft = latestVersion != undefined
  const hasAutomaticInvoiceAccess =
    hasAutomaticInvoiceCapability() &&
    !isOnPremises &&
    !onTSP &&
    ((invoiceType == 1 && invoiceTemplate == 1) ||
      (invoiceType != 1 && automaticSupportedTemplates.includes(invoiceTemplate))) &&
    (!companyFormModes ||
      companyFormModes.length == 0 ||
      (invoiceType == 1
        ? companyFormModes.includes(1)
        : companyFormModes.includes(2) || companyFormModes.includes(3)))

  const importTabs = useMemo(() => {
    const tabs: Array<{
      mode: ExcelImportMode
      title: string
      subtitle: string
      version: number
      draftUrl: string
      color: string
      icon: string
    }> = []

    if (hasOldDraft) {
      tabs.push({
        mode: 'old',
        title: 'نسخه قدیمی',
        subtitle: 'مناسب فایل‌های اکسل قدیمی',
        version: -1,
        draftUrl: `${API_ROOT}/files/old/InvoiceDraft${draftKey}.xlsx?${rnd}`,
        color: 'warning',
        icon: 'la-history',
      })
    }

    if (hasNewDraft) {
      tabs.push({
        mode: 'new',
        title: 'نسخه جدید',
        subtitle: `آخرین نسخه الگو: ${padVersion(latestVersion)}`,
        version: latestVersion,
        draftUrl: `${API_ROOT}/files/new/InvoiceDraft${draftKey}Ver${padVersion(
          latestVersion
        )}.xlsx?${rnd}`,
        color: 'success',
        icon: 'la-file-excel',
      })
    }

    if (hasAutomaticInvoiceAccess) {
      tabs.push({
        mode: 'automatic',
        title: 'صورتحساب خودکار',
        subtitle:
          invoiceType == 1
            ? 'ورود شماره، تاریخ، خریدار و قیمت نهایی'
            : 'ورود شماره، تاریخ و قیمت نهایی',
        version: latestVersion || 1,
        draftUrl: `${API_ROOT}/files/${
          invoiceType == 1 ? automaticTypeOneDraftFileName : automaticDraftFileName
        }?${rnd}`,
        color: 'primary',
        icon: 'la-magic',
      })
    }

    return tabs
  }, [
    draftKey,
    hasAutomaticInvoiceAccess,
    hasNewDraft,
    hasOldDraft,
    invoiceType,
    latestVersion,
    rnd,
  ])

  const activeTab = importTabs.find((tab) => tab.mode == importMode) || importTabs[0]

  // The tabs' arrow keys follow the page's direction: in RTL the next tab is on the left.
  const moveTabFocus = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const rtl = window.getComputedStyle(event.currentTarget).direction === 'rtl'
    const last = importTabs.length - 1
    let next: number | undefined
    if (event.key === 'ArrowLeft') next = rtl ? index + 1 : index - 1
    else if (event.key === 'ArrowRight') next = rtl ? index - 1 : index + 1
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = last
    if (next === undefined) return

    event.preventDefault()
    next = next < 0 ? last : next > last ? 0 : next
    setImportMode(importTabs[next].mode)
    const tabs = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
    tabs?.[next]?.focus()
  }

  const isAutomaticMode = activeTab?.mode == 'automatic'
  // «پیش‌فاکتور»: chosen here for the whole file (the template is the invoice one); never sent. The
  // automatic cost-price import always registers invoices.
  const proformaUpload = proforma && !isAutomaticMode

  useEffect(() => {
    axios.get(`${API_URL}/taxpayer/printTemplate/`).then((response) => {
      setTemplateOptions(response.data)
    })
  }, [])

  useEffect(() => {
    const _templateItems: SearchSelectOption[] = []
    templateOptions.forEach((itm) => {
      _templateItems.push({
        label: itm.name,
        value: itm.id,
      })
    })
    setTemplateItems(_templateItems)
  }, [templateOptions])

  useEffect(() => {
    if (importTabs.length > 0 && !importTabs.some((tab) => tab.mode == importMode)) {
      setImportMode(importTabs[0].mode)
    }
  }, [importMode, importTabs])

  useEffect(() => {
    submit()
  }, [file])

  // The fee was asked for a file of proformas: another choice of what the file is drops it.
  useEffect(() => {
    if (!proformaUpload) setProformaCharge(undefined)
  }, [proformaUpload])

  const resetUploadForm = () => {
    setFile(null)
    setSelectedFileName('')
    setServerMessage('')
    setUploadProgress(0)
    setUploadStatus('idle')
  }

  function submit(chosen: File | null = file, confirmCharge = false) {
    if (!chosen || !activeTab || uploadStatus == 'uploading') {
      return
    }

    setUploadStatus('uploading')
    setUploadProgress(0)
    setServerMessage('')
    setWalletShort(undefined)
    setProformaCharge(undefined)
    setSelectedFileName(chosen.name)

    const data = new FormData()
    data.append('file', chosen, chosen.name)
    data.append('invoiceType', invoiceType.toString())
    data.append('invoiceTemplate', invoiceTemplate.toString())
    data.append('printTemplate', printTemplate.toString())
    if (proformaUpload) {
      data.append('proforma', 'true')
      data.append('send', 'false')
      if (confirmCharge) data.append('confirmCharge', 'true')
    } else {
      const requestedSend = sendTouched ? send : defaultAutoSend
      if (requestedSend !== undefined) {
        data.append('send', requestedSend ? 'true' : 'false')
      }
    }
    data.append('vatCheck', vatCheck == 1 ? 'true' : 'false')
    data.append('validationMode', validationMode.toString())
    if (checkTypeTwoBuyers && activeTab.mode != 'automatic') {
      data.append('checkTypeTwoBuyers', 'true')
    }
    data.append('version', activeTab.version.toString())
    data.append('importMode', activeTab.mode == 'automatic' ? 'automaticCostPrice' : 'invoice')

    axios
      .post(POST_INVOICE_EXCEL_URL, data, {
        onUploadProgress: (event) => {
          if (!event.total) {
            return
          }

          setUploadProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)))
        },
      })
      .then((response) => {
        const result = response.data
        if (result?.isSuccess) {
          setServerMessage(readResponseMessage(result, queuedUploadMessage))
          setUploadStatus('queued')
        } else {
          setServerMessage(readResponseMessage(result, uploadErrorMessage))
          setUploadStatus('error')
        }
        setFile(null)
      })
      .catch((error) => {
        const service = readServiceError(error, '')
        if (service.code === CHARGE_CONFIRM) {
          // The fee of each proforma of this file, agreed to once; the same file is sent again.
          setProformaCharge({
            file: chosen,
            prompt: {amount: service.amount || 0, items: service.items || [], message: service.message, perUse: true},
          })
          setUploadStatus('idle')
          setFile(null)
          return
        }
        setWalletShort(
          service.code === WALLET_INSUFFICIENT
            ? {balance: service.balance, required: service.required}
            : undefined
        )
        setServerMessage(service.code && service.message ? service.message : readUploadErrorMessage(error))
        setUploadStatus('error')
        setFile(null)
      })
  }

  return (
    <>
      <div className='row'>
        {uploadStatus == 'uploading' && (
          <div className='col-12'>
            <div className='d-flex flex-column align-items-center text-center py-10'>
              <span className='symbol symbol-80px bg-light-primary mb-6'>
                <span className='symbol-label'>
                  <i className='las la-cloud-upload-alt fs-3x text-primary'></i>
                </span>
              </span>
              <h3 className='text-gray-900 mb-3'>
                {uploadProgress >= 100 ? 'در حال ثبت درخواست در صف' : 'در حال ارسال فایل اکسل'}
              </h3>
              {selectedFileName && (
                <span className='badge badge-light-primary fs-7 mb-5'>{selectedFileName}</span>
              )}
              <p className='text-gray-700 fs-6 mb-8'>
                {uploadProgress >= 100
                  ? 'فایل کامل به سرور رسید؛ چند لحظه دیگر نتیجه ثبت در صف نمایش داده می‌شود.'
                  : 'پس از ارسال کامل فایل، درخواست ثبت در صف پردازش می‌شود.'}
              </p>
              <div className='progress w-75 h-8px'>
                <div
                  className='progress-bar progress-bar-striped progress-bar-animated rounded'
                  style={{width: `${uploadProgress || 100}%`}}
                ></div>
              </div>
            </div>
          </div>
        )}
        {(uploadStatus == 'queued' || uploadStatus == 'error') && (
          <div className='col-12'>
            <div className='d-flex flex-column align-items-center text-center py-8'>
              <span
                className={clsx(
                  'symbol symbol-80px mb-6',
                  uploadStatus == 'queued' ? 'bg-light-success' : 'bg-light-danger'
                )}
              >
                <span className='symbol-label'>
                  <i
                    className={clsx(
                      'las fs-3x',
                      uploadStatus == 'queued'
                        ? 'la-check-circle text-success'
                        : 'la-exclamation-circle text-danger'
                    )}
                  ></i>
                </span>
              </span>
              <h3
                className={clsx('mb-3', uploadStatus == 'queued' ? 'text-success' : 'text-danger')}
              >
                {uploadStatus == 'queued' ? 'فایل در صف پردازش قرار گرفت' : 'آپلود انجام نشد'}
              </h3>
              {selectedFileName && (
                <span className='badge badge-light fs-7 mb-5'>{selectedFileName}</span>
              )}
              <div
                className={clsx(
                  'alert w-100 text-start mb-0',
                  uploadStatus == 'queued'
                    ? 'alert-light-success border border-success border-dashed'
                    : 'alert-light-danger border border-danger border-dashed'
                )}
                style={{whiteSpace: 'pre-line'}}
              >
                {serverMessage}
                {walletShort && Number(currentUser?.cpi || 0) > 0 && (
                  <div className='mt-3'>
                    <WalletShortfallAction
                      companyId={Number(currentUser?.cpi)}
                      balance={walletShort.balance}
                      required={walletShort.required}
                      actionLabel='بارگذاری فایل'
                    />
                  </div>
                )}
              </div>
              <div className='d-flex flex-wrap justify-content-center gap-3 mt-8'>
                {uploadStatus == 'queued' && (
                  <Link className='btn btn-primary' to='/apps/log-management/logs'>
                    <i className='las la-list-alt fs-2 me-2'></i>
                    مشاهده لاگ‌ها
                  </Link>
                )}
                <button
                  type='button'
                  className={clsx(
                    'btn',
                    uploadStatus == 'queued' ? 'btn-light-primary' : 'btn-primary'
                  )}
                  onClick={resetUploadForm}
                >
                  <i className='las la-redo-alt fs-2 me-2'></i>
                  {uploadStatus == 'queued' ? 'آپلود فایل دیگر' : 'انتخاب فایل دیگر'}
                </button>
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={() => action.setExcelModal(false)}
                >
                  بستن
                </button>
              </div>
            </div>
          </div>
        )}
        {uploadStatus == 'idle' && (
          <div className='col-12'>
            {importTabs.length > 0 ? (
              <>
                <InvoiceExcelArticle9Notice />
                {/* A segmented control of equal parts (509): the line tabs were cut on both sides on a phone. */}
                <div
                  className='mp-excel-mode-tabs'
                  role='tablist'
                  aria-label='نسخهٔ الگوی اکسل'
                  style={{gridTemplateColumns: `repeat(${importTabs.length}, minmax(0, 1fr))`}}
                >
                  {importTabs.map((tab, index) => {
                    const selected = activeTab?.mode == tab.mode
                    return (
                      <button
                        key={tab.mode}
                        type='button'
                        role='tab'
                        id={`invoice-excel-tab-${tab.mode}`}
                        aria-selected={selected}
                        aria-controls='invoice-excel-panel'
                        tabIndex={selected ? 0 : -1}
                        className={clsx('mp-excel-mode-tab', {'is-active': selected})}
                        onClick={() => setImportMode(tab.mode)}
                        onKeyDown={(event) => moveTabFocus(event, index)}
                      >
                        <i className={clsx('las', tab.icon, `text-${tab.color}`)} aria-hidden='true'></i>
                        <span>{tab.title}</span>
                      </button>
                    )
                  })}
                </div>

                <div
                  role='tabpanel'
                  id='invoice-excel-panel'
                  aria-labelledby={activeTab ? `invoice-excel-tab-${activeTab.mode}` : undefined}
                >
                  <div className='row align-items-start'>
                    <div className='col-12 col-lg-6 text-start'>
                      <h2 className='w-100 text-center'>تنظیمات بارگذاری</h2>
                      <div className='d-flex flex-column w-100 mt-5'>
                        {/* The bank's own install has no proforma (the user, 2026-10-26). */}
                        {!isAutomaticMode && !isOnPremises && (
                          <div className='w-100 mb-5'>
                            <CustomInputs
                              type='toggle'
                              isVertical
                              label='ثبت ردیف‌های این فایل به‌عنوان پیش‌فاکتور'
                              stateValue={{
                                value: proforma,
                                setValue: setProforma,
                              }}
                              hint='الگوی فایل همان الگوی صورتحساب است. پیش‌فاکتورها ارسال نمی‌شوند، حافظه مالیاتی و شماره منحصربه‌فرد مالیاتی ندارند و با مدت اعتبار پیش‌فرض کسب‌وکار ثبت می‌شوند؛ فقط ردیف‌های با موضوع «اصلی» پذیرفته می‌شوند. پیش از انتخاب فایل تعیین کنید.'
                            />
                            {proforma && Number(currentUser?.cpi || 0) > 0 && (
                              <ServiceFeeLine
                                companyId={Number(currentUser?.cpi)}
                                feature='proforma'
                                each='هر پیش‌فاکتور این فایل'
                                className='mt-2'
                              />
                            )}
                          </div>
                        )}
                        {templateItems && (
                          <CustomInputs
                            label={proformaUpload ? 'قالب چاپ پیش‌فاکتور' : 'قالب چاپ صورتحساب'}
                            type='select'
                            items={templateItems}
                            stateValue={{
                              value: printTemplate,
                              setValue: setPrintTemplate,
                            }}
                          />
                        )}
                        <div className='w-100 mt-5'>
                          {proformaUpload ? (
                            <p className='text-muted fs-7 mb-0' role='status'>
                              <i className='las la-info-circle me-1' aria-hidden='true'></i>
                              پیش‌فاکتور به سامانه مؤدیان ارسال نمی‌شود؛ پس از تبدیل به صورتحساب در
                              فهرست، صورتحساب حاصل ارسال می‌شود.
                            </p>
                          ) : (
                            <CustomInputs
                              type='toggle'
                              isVertical
                              label={'ارسال مستقیم صورتحساب‌ها به سامانه مؤدیان'}
                              stateValue={{
                                value: displayedSend,
                                setValue: setSendFromControl,
                              }}
                              hint='مقدار اولیه از تنظیم روش ورود این فایل گرفته شده است؛ تغییر آن روی همه صورتحساب‌های همین فایل اعمال می‌شود.'
                            />
                          )}
                        </div>
                        {!isAutomaticMode && (
                          <div className='w-100 mt-5'>
                            <CustomInputs
                              type='radio'
                              isVertical
                              label={
                                'برای تطبیق نرخ مالیات بر ارزش افزوده وارد شده در شیت صورتحساب، یکی از گزینه‌های زیر را انتخاب کنید:'
                              }
                              items={[
                                {
                                  label:
                                    'تطبیق خودکار نرخ مالیات با جدول شناسه کالا/خدمت و نمایش خطا در صورت مغایرت',
                                  value: 1,
                                },
                                {
                                  label: 'استفاده از نرخ مالیات وارد شده در فایل، بدون تطبیق',
                                  value: 2,
                                },
                              ]}
                              stateValue={{
                                value: vatCheck,
                                setValue: setVatCheck,
                              }}
                            />
                          </div>
                        )}
                        <div className='w-100 mt-5'>
                          <CustomInputs
                            type='radio'
                            isVertical
                            label='در صورت وجود خطا در اعتبارسنجی فایل، عملیات چگونه ادامه پیدا کند؟'
                            items={[
                              {
                                label:
                                  'توقف کل عملیات؛ اگر حتی یک ردیف خطا داشته باشد، هیچ صورتحسابی وارد نشود',
                                value: 0,
                              },
                              {
                                label:
                                  'ورود ردیف‌های سالم؛ ردیف‌های خطادار رد شوند و سایر صورتحساب‌ها پردازش شوند',
                                value: 1,
                              },
                            ]}
                            stateValue={{
                              value: validationMode,
                              setValue: setValidationMode,
                            }}
                            hint='حالت پیش‌فرض توقف کل عملیات است. در حالت ورود ردیف‌های سالم، گزارش ردیف‌های ردشده و نتیجه صدور در صفحه لاگ‌ها در دسترس خواهد بود.'
                          />
                        </div>
                        {!isOnPremises && activeTab.mode != 'automatic' && (
                          <div className='w-100 mt-5'>
                            <div className='form-check form-check-custom form-check-solid align-items-start'>
                              <input
                                className='form-check-input mt-1'
                                type='checkbox'
                                id={`check-type-two-buyers-${rnd}`}
                                checked={checkTypeTwoBuyers}
                                onChange={(event) => setCheckTypeTwoBuyers(event.target.checked)}
                              />
                              <label
                                className='form-check-label text-gray-800'
                                htmlFor={`check-type-two-buyers-${rnd}`}
                              >
                                پرونده مالیاتی خریداران حقیقیِ صورتحساب‌های نوع ۲ هم بررسی شود
                                <span className='d-block text-muted fs-7 mt-1'>
                                  در هر فایل حداکثر ۵ کد ملیِ بدون شماره اقتصادی استعلام می‌شود؛ با این
                                  گزینه خریداران نوع ۲ هم در همین سقف حساب می‌شوند و اگر پرونده داشته باشند
                                  فقط هشدار می‌گیرند. صورتحساب نوع ۱ بعد از این ۵ کد ملی رد می‌شود.
                                </span>
                              </label>
                            </div>
                            {Number(currentUser?.cpi || 0) > 0 && (
                              <ServiceFeeLine
                                companyId={Number(currentUser?.cpi)}
                                feature='buyerTaxFileInquiry'
                                each='هر استعلام پرونده خریدار'
                                className='mt-2'
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={clsx('text-center col-12 col-lg-6 mt-8 mt-lg-0')}>
                      <h2 className='mb-4 text-gray-800'>بارگذاری فایل تکمیل‌شده</h2>
                      <label className='btn btn-light-primary btn-lg d-inline-flex align-items-center'>
                        <i className='las la-cloud-upload-alt fs-2 me-2'></i>
                        انتخاب فایل اکسل
                        <input
                          type='file'
                          accept='.xlsx'
                          className='d-none'
                          onChange={(e) => {
                            const selectedFile = e.target.files?.[0]
                            if (!selectedFile) {
                              setFile(null)
                              return
                            }

                            setServerMessage('')
                            setSelectedFileName(selectedFile.name)
                            setFile(selectedFile)
                            e.target.value = ''
                          }}
                        />
                      </label>
                      <div className='text-gray-600 fs-7 mt-4'>
                        فایل باید با پسوند xlsx و مطابق الگوی همین بخش باشد.
                      </div>
                      {proformaUpload && proformaCharge && (
                        <div className='text-start'>
                          <InlineChargeConfirm
                            prompt={proformaCharge.prompt}
                            action={`ثبت هر پیش‌فاکتور فایل «${proformaCharge.file.name}»`}
                            confirmLabel='تأیید و بارگذاری'
                            walletAction='بارگذاری فایل'
                            onConfirm={() => submit(proformaCharge.file, true)}
                            onCancel={() => {
                              setProformaCharge(undefined)
                              setSelectedFileName('')
                            }}
                          />
                          <div className='text-muted fs-8 mt-2'>
                            با یک بار تأیید، همهٔ پیش‌فاکتورهای این فایل ثبت می‌شوند. هزینه هنگام ثبت هر
                            پیش‌فاکتور گرفته می‌شود؛ استفاده‌های رایگان بستهٔ خدمات و هدیه‌ها پیش از کیف پول حساب
                            می‌شوند و ردیف‌های ثبت‌نشده هزینه‌ای ندارند.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='separator border-gray-400 my-10'></div>
                  <div className='row align-items-start'>
                    <div className='col-12 col-sm-4 text-center'>
                      <h2 className='mb-3 text-gray-800'>فایل نمونه</h2>
                      <a className='mx-2' href={activeTab?.draftUrl} target='_blank' download>
                        <i
                          className={clsx(
                            'las la-file-excel me-4 fs-5x',
                            `text-${activeTab?.color || 'success'}`
                          )}
                        ></i>
                        <br />
                        <span className={clsx('fs-3', `text-${activeTab?.color || 'success'}`)}>
                          دانلود الگوی اکسل
                        </span>
                        <br />
                        <span className='text-gray-600 fs-6'>{activeTab?.subtitle}</span>
                      </a>
                      <ExcelColumnGuideHint />
                    </div>
                    <div className='col-12 col-sm-8 text-start p-5'>
                      {activeTab?.mode == 'new' && (
                        <div className='text-gray-600 fs-6'>
                          <h5 className='text-gray-800'>ویژگی‌های نسخه جدید:</h5>
                          <ul className='list-unstyled ms-5'>
                            <li>- پشتیبانی از خریداران مشارکت مدنی</li>
                            <li>- پشتیبانی از اتباع خارجی</li>
                            <li>- استفاده از آخرین ساختار الگوی اکسل همین صورتحساب</li>
                          </ul>
                        </div>
                      )}
                      {activeTab?.mode == 'old' && (
                        <div className='text-gray-600 fs-6'>
                          <h5 className='text-gray-800'>ورود فایل‌های قدیمی:</h5>
                          <p className='ms-5' style={{textAlign: 'justify'}}>
                            این گزینه برای بارگذاری فایل‌هایی است که قبلاً با نمونه قدیمی تهیه
                            شده‌اند. فایل قدیمی ستون ماده ۹ ندارد؛ برای فایل تازه، نمونه «نسخه جدید»
                            را دانلود کنید.
                          </p>
                        </div>
                      )}
                      {activeTab?.mode == 'automatic' && (
                        <div className='text-gray-600 fs-6'>
                          <h5 className='text-gray-800'>قالب فایل صورتحساب خودکار:</h5>
                          <ul className='list-unstyled ms-5'>
                            <li>- شماره صورتحساب</li>
                            <li>- تاریخ و ساعت صدور</li>
                            {invoiceType == 1 && <li>- شناسه خریدار</li>}
                            <li>- مبلغ نهایی صورتحساب به ریال</li>
                            <li>
                              - ماده ۹ و تاریخ و زمان ثبت صورتحساب، فقط وقتی مهلت ارسال گذشته است
                            </li>
                            {invoiceTemplate == 13 && (
                              <li>
                                - شناسه یکتای بیمه نامه (الزامی) و شناسه یکتای الحاقیه، برای هر
                                صورتحساب
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className='alert alert-warning text-start'>
                برای این نوع و الگوی صورتحساب، فایل نمونه اکسل فعالی پیدا نشد.
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export {InvoiceExcelModalForm}
